/**
 * Générateur — Connaître la définition de la bissectrice d'un angle saillant
 * Attendu ID : 664 | Niveau : 6ème | Domaine : Espace et géométrie
 *
 * Types générés : qcm_unique, texte, relier, tableau_vf, texte_trou_drag
 * Labels de référence :
 *   - Reconnaître visuellement la bissectrice d'un angle
 *   - Coder les angles égaux sur une figure avec bissectrice
 *   - Compléter des phrases avec le vocabulaire de la bissectrice
 *   - Nommer les bissectrices sur une figure codée et justifier
 * Variantes estimées : 200+ (objectif : 20/20 uniques sur 20 itérations auditGen)
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle, pickLettresSains,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeRelier,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 664;
const LETTRES = 'ABCDEFGHJKLMNPQRSTUVWYZ'.split('');
const ANGLES = [20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 160];

const L_DEF = 'Connaître la définition de la bissectrice d\'un angle saillant';
const L_RECO = 'Reconnaître visuellement la bissectrice d\'un angle';
const L_NOMMER = 'Nommer les bissectrices sur une figure codée';

const opts = (label) => ({
    niveau: '6eme',
    matiere: 'maths',
    attendu: ATTENDU_ID,
    label: label || L_DEF,
});

const pickLetters = (n) => pickLettresSains(LETTRES, n);

const svgToDataUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

// ─── SVG Helper ──────────────────────────────────────────────
const svgAngleBissectrice = (vertex, rayLen, angleDeg, halfDeg, names) => {
    const [vName, xName, yName, tName] = names;
    const vx = 120, vy = 170;
    const toRad = (d) => d * Math.PI / 180;

    const x1 = vx + rayLen * Math.cos(toRad(0));
    const y1 = vy - rayLen * Math.sin(toRad(0));
    const x2 = vx + rayLen * Math.cos(toRad(angleDeg));
    const y2 = vy - rayLen * Math.sin(toRad(angleDeg));
    const tx = vx + rayLen * Math.cos(toRad(halfDeg));
    const ty = vy - rayLen * Math.sin(toRad(halfDeg));

    const arcR = 30;
    const a1x = vx + arcR * Math.cos(toRad(0));
    const a1y = vy - arcR * Math.sin(toRad(0));
    const a2x = vx + arcR * Math.cos(toRad(halfDeg));
    const a2y = vy - arcR * Math.sin(toRad(halfDeg));
    const a3x = vx + arcR * Math.cos(toRad(angleDeg));
    const a3y = vy - arcR * Math.sin(toRad(angleDeg));

    const largeArc1 = halfDeg > 180 ? 1 : 0;
    const largeArc2 = (angleDeg - halfDeg) > 180 ? 1 : 0;

    return `<svg viewBox='0 0 300 200' xmlns='http://www.w3.org/2000/svg'>`
        + `<line x1='${vx}' y1='${vy}' x2='${x1}' y2='${y1}' stroke='black' stroke-width='2'/>`
        + `<line x1='${vx}' y1='${vy}' x2='${x2}' y2='${y2}' stroke='black' stroke-width='2'/>`
        + `<line x1='${vx}' y1='${vy}' x2='${tx}' y2='${ty}' stroke='#1D9E75' stroke-width='2' stroke-dasharray='6,3'/>`
        + `<text x='${vx - 15}' y='${vy + 15}' font-size='14'>${vName}</text>`
        + `<text x='${x1 + 5}' y='${y1 + 5}' font-size='12' font-style='italic'>${xName}</text>`
        + `<text x='${x2 + 5}' y='${y2 - 5}' font-size='12' font-style='italic'>${yName}</text>`
        + `<text x='${tx + 5}' y='${ty}' font-size='12' fill='#1D9E75' font-style='italic'>${tName}</text>`
        // Arc marks for equal angles
        + `<path d='M ${a1x} ${a1y} A ${arcR} ${arcR} 0 ${largeArc1} 0 ${a2x} ${a2y}' fill='none' stroke='#E24B4A' stroke-width='1.5'/>`
        + `<path d='M ${a2x} ${a2y} A ${arcR} ${arcR} 0 ${largeArc2} 0 ${a3x} ${a3y}' fill='none' stroke='#E24B4A' stroke-width='1.5'/>`
        // Tick marks on arcs
        + `<line x1='${vx + (arcR - 3) * Math.cos(toRad(halfDeg / 2))}' y1='${vy - (arcR - 3) * Math.sin(toRad(halfDeg / 2))}' x2='${vx + (arcR + 3) * Math.cos(toRad(halfDeg / 2))}' y2='${vy - (arcR + 3) * Math.sin(toRad(halfDeg / 2))}' stroke='#E24B4A' stroke-width='1.5'/>`
        + `<line x1='${vx + (arcR - 3) * Math.cos(toRad(halfDeg + (angleDeg - halfDeg) / 2))}' y1='${vy - (arcR - 3) * Math.sin(toRad(halfDeg + (angleDeg - halfDeg) / 2))}' x2='${vx + (arcR + 3) * Math.cos(toRad(halfDeg + (angleDeg - halfDeg) / 2))}' y2='${vy - (arcR + 3) * Math.sin(toRad(halfDeg + (angleDeg - halfDeg) / 2))}' stroke='#E24B4A' stroke-width='1.5'/>`
        + `</svg>`;
};

// ─── SVG Helper — grille 5 mini-figures (a/b/c/d/e) ─────────
const svgMiniFigureBissectrice = (cx, cy, orientationDeg, angleDeg, redOffsetDeg, label, labelX, labelY) => {
    const rayLen = 55;
    const toRad = (d) => d * Math.PI / 180;
    const a1 = orientationDeg;
    const a2 = orientationDeg + angleDeg;
    const redAngle = orientationDeg + angleDeg / 2 + redOffsetDeg;

    const x1 = cx + rayLen * Math.cos(toRad(a1));
    const y1 = cy - rayLen * Math.sin(toRad(a1));
    const x2 = cx + rayLen * Math.cos(toRad(a2));
    const y2 = cy - rayLen * Math.sin(toRad(a2));
    const xr = cx + rayLen * Math.cos(toRad(redAngle));
    const yr = cy - rayLen * Math.sin(toRad(redAngle));

    return `<text x='${labelX}' y='${labelY}' font-size='14' fill='#eab308' font-weight='bold'>${label}.</text>`
        + `<line x1='${cx}' y1='${cy}' x2='${x1.toFixed(1)}' y2='${y1.toFixed(1)}' stroke='black' stroke-width='1.8'/>`
        + `<line x1='${cx}' y1='${cy}' x2='${x2.toFixed(1)}' y2='${y2.toFixed(1)}' stroke='black' stroke-width='1.8'/>`
        + `<line x1='${cx}' y1='${cy}' x2='${xr.toFixed(1)}' y2='${yr.toFixed(1)}' stroke='#E24B4A' stroke-width='1.8'/>`;
};

const svgReconnaissanceBissectrices = (figures) => {
    const positions = [
        { cx: 90,  cy: 80,  lx: 25,  ly: 45  },
        { cx: 250, cy: 80,  lx: 185, ly: 45  },
        { cx: 410, cy: 80,  lx: 345, ly: 45  },
        { cx: 90,  cy: 210, lx: 25,  ly: 175 },
        { cx: 250, cy: 210, lx: 185, ly: 175 },
    ];
    const labels = ['a', 'b', 'c', 'd', 'e'];
    const body = figures.map((f, i) => {
        const p = positions[i];
        return svgMiniFigureBissectrice(p.cx, p.cy, f.orientationDeg, f.angleDeg, f.redOffsetDeg, labels[i], p.lx, p.ly);
    }).join('');
    return `<svg viewBox='0 0 500 270' xmlns='http://www.w3.org/2000/svg' width='500' height='270'>${body}</svg>`;
};

// ─── Générateurs ─────────────────────────────────────────────

// QCM — Variante définition : reconnaître la bonne définition
const genDefQcmDefinition = () => {
    const [V, X, Y] = pickLetters(3);

    const bonneDefinition = `C'est la demi-droite qui partage l'angle $\\widehat{${X}${V}${Y}}$ en deux angles adjacents de même mesure.`;

    const faussesDefs = [
        `C'est la droite perpendiculaire au côté [${V}${X}) de l'angle $\\widehat{${X}${V}${Y}}$.`,
        `C'est la droite qui coupe le segment [${X}${Y}] en deux parties égales.`,
        `C'est la demi-droite qui partage l'angle $\\widehat{${X}${V}${Y}}$ en deux angles supplémentaires.`,
        `C'est le segment qui relie ${X} à ${Y} en passant par ${V}.`,
        `C'est la droite passant par le milieu de [${X}${Y}] perpendiculairement à [${X}${Y}].`,
    ];

    const choixFaux = shuffle(faussesDefs).slice(0, 3);
    // makeQcm attend le CONTENU de la bonne réponse (il mélange et calcule la
    // lettre lui-même) — lui passer une lettre produit bonne_reponse undefined.
    const texte = `<p>Parmi les phrases suivantes, laquelle définit correctement la <b>bissectrice</b> de l'angle $\\widehat{${X}${V}${Y}}$ ?</p>`;

    const payload = makeQcm([bonneDefinition, ...choixFaux], bonneDefinition, texte, opts(L_DEF));
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: 'phrases' };
    payload._meta.generatorKey = 'definition_bissectrice_qcm_unique';
    return payload;
};

// QCM — Variante figure : identifier la bissectrice sur figure codée
const genDefQcmFigure = () => {
    const [V, X, Y, T] = pickLetters(4);
    const angle = randomChoice(ANGLES);
    const half = angle / 2;

    const bonne = `[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$.`;
    const faux = shuffle([
        `[${V}${T}) est un côté de l'angle $\\widehat{${X}${V}${Y}}$.`,
        `[${V}${T}) est la médiatrice du segment [${X}${Y}].`,
        `On ne peut rien affirmer sans mesurer au rapporteur.`,
        `[${V}${X}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$.`,
    ]).slice(0, 3);

    const texte = `<p>Sur la figure ci-dessous, l'angle $\\widehat{${X}${V}${Y}}$ mesure ${angle}° et l'angle $\\widehat{${X}${V}${T}}$ mesure ${half}°. Que peut-on affirmer ?</p>{{element:0}}`;

    const svg = svgAngleBissectrice(V, 120, angle, half, [V, X, Y, T]);

    // makeQcm attend le CONTENU de la bonne réponse (mélange + lettre gérés par lui)
    const payload = makeQcm([bonne, ...faux], bonne, texte, {
        ...opts(L_DEF),
    });
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'image', config: { imageData: svgToDataUrl(svg) } }],
    };
    // sousType STRUCTUREL : pose par la branche qui a choisi le modele,
    // il route le coup de pouce cote eleve. Canal `contenu`, qui survit
    // au POST — jamais `_meta`, supprime a la sauvegarde.
    payload.contenu = { ...payload.contenu, sousType: 'figure' };
    payload._meta.generatorKey = 'definition_bissectrice_qcm_unique';
    return payload;
};

// Saisie — Nommer la demi-droite (vocabulaire)
const genDefSaisie = () => {
    const [V, X, Y, T] = pickLetters(4);
    const angle = randomChoice(ANGLES);
    const half = angle / 2;

    const variante = randomChoice(['nommer', 'axe']);

    let texte, bonneReponse;
    if (variante === 'nommer') {
        texte = `<p>Sur la figure ci-dessous, les angles $\\widehat{${X}${V}${T}}$ et $\\widehat{${T}${V}${Y}}$ sont codés égaux. Comment appelle-t-on la demi-droite [${V}${T}) ?</p>{{element:0}}`;
        bonneReponse = 'bissectrice';
    } else {
        texte = `<p>[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$. Quel mot complète la phrase : « la bissectrice est un … de symétrie de l'angle » ?</p>`;
        bonneReponse = 'axe';
    }

    const svg = svgAngleBissectrice(V, 120, angle, half, [V, X, Y, T]);

    const payload = makeSaisie(bonneReponse, texte, {
        ...opts(L_DEF),
        strict: false,
    });
    if (variante === 'nommer') {
        payload.elements_additionnels = {
            version: 1,
            elements: [{ type: 'image', config: { imageData: svgToDataUrl(svg) } }],
        };
    }
    payload._meta.generatorKey = 'definition_bissectrice_texte';
    return payload;
};

// Relier — Termes ↔ définitions
const genDefRelier = () => {
    // La paire BISSECTRICE est forcée dans chaque tirage : c'est le sujet de
    // l'attendu. Sans elle, un tirage sur deux ne contenait pas le mot (mesuré
    // à 50,1 %) et l'exercice sortait de son attendu.
    const paireSujet = {
        source: 'Bissectrice d\'un angle',
        target: 'Demi-droite qui partage l\'angle en deux angles adjacents de même mesure',
    };
    // « Médiane d'un triangle » a été retirée du pool le 2026-08-02 : droite
    // remarquable du triangle, hors du champ de ce cluster. « Cercle circonscrit »
    // l'a remplacée : même cluster, et plus proche de la définition visée.
    // NE PAS ajouter de source « Demi-droite » : la cible de la paire sujet
    // commence par ce mot, un élève qui apparie au mot-clé les relierait
    // ensemble. Le pool de complément compte 7 paires, soit C(7,3) = 35
    // combinaisons avec la paire sujet forcée.
    const autresPaires = [
        { source: 'Médiatrice d\'un segment', target: 'Droite perpendiculaire au segment passant par son milieu' },
        { source: 'Axe de symétrie', target: 'Droite telle que la figure est sa propre image par pliage' },
        { source: 'Angles adjacents', target: 'Angles ayant le même sommet et un côté commun' },
        { source: 'Angle saillant', target: 'Angle dont la mesure est comprise entre 0° et 180° exclu' },
        { source: 'Angles supplémentaires', target: 'Deux angles dont la somme des mesures vaut 180°' },
        { source: 'Angle droit', target: 'Angle dont la mesure vaut exactement 90°' },
        { source: 'Cercle circonscrit', target: 'Cercle passant par les trois sommets d\'un triangle' },
    ];

    const pairs = shuffle([paireSujet, ...randomChoiceMultiple(autresPaires, 3)]);
    const texte = `<p>Relie chaque terme géométrique à sa définition.</p>`;

    const payload = makeRelier(pairs, texte, opts(L_DEF));
    payload._meta.generatorKey = 'definition_bissectrice_relier';
    return payload;
};

// Tableau V/F
const genDefVF = () => {
    const [V, X, Y, T] = pickLetters(4);

    const vrais = shuffle([
        `La bissectrice d'un angle le partage en deux angles adjacents de même mesure.`,
        `La bissectrice d'un angle est un axe de symétrie de cet angle.`,
        // ⚠️ pas de '[' brut dans une ligne de tableau V/F : le crochet orphelin de
        // la notation [VT) casse parseComplexAnswer (comptage de profondeur) →
        // réponse juste comptée fausse. $\lbrack$ rend le même crochet via KaTeX.
        `Si les angles $\\widehat{${X}${V}${T}}$ et $\\widehat{${T}${V}${Y}}$ sont égaux, alors $\\lbrack ${V}${T})$ est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$.`,
        `La bissectrice d'un angle est une demi-droite, pas un segment.`,
        `Pour vérifier qu'une demi-droite est la bissectrice, on peut mesurer les deux angles formés au rapporteur.`,
        `Le sommet de l'angle est aussi un point de la bissectrice.`,
    ]);

    const faux = shuffle([
        `La bissectrice partage l'angle en deux angles supplémentaires.`,
        `La bissectrice d'un segment est la droite perpendiculaire passant par son milieu.`,
        `Si une demi-droite semble partager l'angle en deux parties égales, c'est forcément la bissectrice.`,
        `La bissectrice de l'angle $\\widehat{${X}${V}${Y}}$ est la demi-droite $\\lbrack ${V}${X})$.`,
        `La médiatrice d'un segment et la bissectrice d'un angle sont le même objet.`,
        `Deux angles adjacents ont toujours la même mesure.`,
        `La bissectrice d'un angle coupe le côté opposé d'un triangle en deux parties égales.`,
        `La bissectrice est une droite perpendiculaire aux côtés de l'angle.`,
    ]);

    // Tirer 2-3 vrais et 2-3 faux (total 5)
    const nbVrais = randInt(2, 3);
    const nbFaux = 5 - nbVrais;

    const selectedVrais = vrais.slice(0, nbVrais);
    const selectedFaux = faux.slice(0, nbFaux);

    const lignes = [];
    const bonnesReponses = [];
    const mixed = shuffle([
        ...selectedVrais.map(l => ({ l, r: 0 })),
        ...selectedFaux.map(l => ({ l, r: 1 })),
    ]);
    mixed.forEach(({ l, r }) => {
        lignes.push(l);
        bonnesReponses.push(r);
    });

    const texte = `<p>Indique si chaque affirmation est vraie ou fausse.</p>`;
    const payload = makeVraiFaux(lignes, bonnesReponses, texte, opts(L_DEF));
    payload._meta.generatorKey = 'definition_bissectrice_tableau_vf';
    return payload;
};

// Texte à trous (drag)
const genDefTrouDrag = () => {
    const [V, X, Y, T] = pickLetters(4);

    const angle = randomChoice(ANGLES);
    const half = angle / 2;

    const modeles = [
        {
            phrase: `La {{bissectrice}} de l'angle $\\widehat{${X}${V}${Y}}$ est la demi-droite qui partage cet angle en deux angles {{adjacents}} de même {{mesure}}.`,
            distracteurs: ['médiatrice', 'supplémentaires', 'longueur'],
        },
        {
            phrase: `La bissectrice de l'angle $\\widehat{${X}${V}${Y}}$ est aussi un {{axe}} de {{symétrie}} de cet angle. On peut la retrouver par {{pliage}}.`,
            distracteurs: ['centre', 'rotation', 'découpage'],
        },
        {
            phrase: `La {{médiatrice}} du segment [${X}${Y}] est la droite {{perpendiculaire}} à ce segment passant par son {{milieu}}. À ne pas confondre avec la {{bissectrice}} de l'angle $\\widehat{${X}${V}${Y}}$.`,
            distracteurs: ['parallèle', 'sommet', 'médiane'],
        },
        {
            phrase: `[${V}${T}) est la bissectrice de l'angle $\\widehat{${X}${V}${Y}}$. Les angles $\\widehat{${X}${V}${T}}$ et $\\widehat{${T}${V}${Y}}$ sont {{adjacents}} et {{égaux}}. Le point ${V} est le {{sommet}} de l'angle.`,
            distracteurs: ['supplémentaires', 'complémentaires', 'milieu'],
        },
        {
            phrase: `L'angle $\\widehat{${X}${V}${Y}}$ est un angle {{saillant}} car il mesure entre 0° et 180°. Sa {{bissectrice}} le partage en deux angles de même {{mesure}}.`,
            distracteurs: ['rentrant', 'médiatrice', 'longueur'],
        },
        {
            phrase: `[${V}${T}) est la {{bissectrice}} de l'angle $\\widehat{${X}${V}${Y}}$. Cela signifie que les angles $\\widehat{${X}${V}${T}}$ et $\\widehat{${T}${V}${Y}}$ sont {{égaux}}. La bissectrice est un axe de {{symétrie}}.`,
            distracteurs: ['médiane', 'supplémentaires', 'rotation'],
        },
    ];

    const modele = randomChoice(modeles);
    const texte = `<p>Complète les phrases suivantes en glissant les mots proposés.</p>`;

    const payload = makeTexteTrou(modele.phrase, texte, {
        ...opts(L_DEF),
        mode: 'drag',
        distracteurs: modele.distracteurs,
    });
    payload._meta.generatorKey = 'definition_bissectrice_texte_trou_drag';
    return payload;
};

// QCM multi — Reconnaissance visuelle : cocher les figures où la demi-droite rouge est bissectrice
const genReconnaissanceVisuelleQcmMulti = () => {
    const nbBissectrices = randInt(1, 3);
    const indicesBis = shuffle([0, 1, 2, 3, 4]).slice(0, nbBissectrices);
    const estBissectrice = [0, 1, 2, 3, 4].map((i) => indicesBis.includes(i));

    const ANGLES_VISU = [60, 70, 80, 90, 100, 110, 120, 130, 140];
    const OFFSETS_FAUX = [-25, -22, -18, -15, 15, 18, 22, 25];

    const figures = estBissectrice.map((isB) => ({
        angleDeg: randomChoice(ANGLES_VISU),
        orientationDeg: randInt(10, 45),
        redOffsetDeg: isB ? 0 : randomChoice(OFFSETS_FAUX),
    }));

    const svg = svgReconnaissanceBissectrices(figures);
    const imageData = svgToDataUrl(svg);

    const labels = ['a', 'b', 'c', 'd', 'e'];
    const bonnes = labels.filter((_, i) => estBissectrice[i]);
    const texte = `<p>Pour quelle(s) figure(s) la demi-droite <span style='color:#E24B4A'>rouge</span> semble-t-elle être la <b>bissectrice</b> de l'angle ?</p>{{element:0}}`;

    const payload = makeQcm(labels, bonnes, texte, opts(L_RECO), true);
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'image', config: { imageData } }],
    };
    payload._meta.generatorKey = 'reconnaissance_visuelle_bissectrice_qcm_multi';
    return payload;
};

// ─── Nommer les bissectrices sur figure codée (QCM multi) ───
const svgNommerBissectrices = (a, c, salt) => {
    const Ox = 250, Oy = 230, R = 170, Rrapp = 185, Rsect = 58;
    const toRad = (d) => d * Math.PI / 180;
    const pt = (deg, r = R) => ({
        x: Ox + r * Math.cos(toRad(deg)),
        y: Oy - r * Math.sin(toRad(deg)),
    });

    const thA = 180, thB = 180 - a, thC = 180 - 2 * a;
    const thE = c, thD = 2 * c;
    const pA = pt(thA), pB = pt(thB), pC = pt(thC);
    const pD = pt(thD), pE = pt(thE), pF = pt(0);

    // Secteur angulaire coloré (pour colorier les zones d'angles)
    const sector = (deg1, deg2, r, color) => {
        const p1 = pt(deg1, r), p2 = pt(deg2, r);
        const largeArc = Math.abs(deg2 - deg1) > 180 ? 1 : 0;
        return `<path d='M ${Ox} ${Oy} L ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} A ${r} ${r} 0 ${largeArc} 0 ${p2.x.toFixed(1)} ${p2.y.toFixed(1)} Z' fill='${color}' fill-opacity='0.55' stroke='none'/>`;
    };
    const ray = (p) =>
        `<line x1='${Ox}' y1='${Oy}' x2='${p.x.toFixed(1)}' y2='${p.y.toFixed(1)}' stroke='black' stroke-width='1.8'/>`;
    const lbl = (p, txt, dx, dy) =>
        `<text x='${(p.x + dx).toFixed(1)}' y='${(p.y + dy).toFixed(1)}' font-size='15' font-style='italic' font-weight='500'>${txt}</text>`;
    const degTxt = (deg, r, txt) => {
        const x = Ox + r * Math.cos(toRad(deg));
        const y = Oy - r * Math.sin(toRad(deg));
        return `<text x='${x.toFixed(1)}' y='${(y + 4).toFixed(1)}' font-size='10' text-anchor='middle' fill='#1f2937' font-weight='600'>${txt}</text>`;
    };

    // Mêmes couleurs pour angles égaux (paire gauche : a / paire droite : c)
    const COLOR_LEFT = '#fbbf24';   // amber-400 — angles de mesure a
    const COLOR_RIGHT = '#34d399';  // emerald-400 — angles de mesure c

    const rapporteur = `<path d='M ${(Ox - Rrapp).toFixed(1)} ${Oy} A ${Rrapp} ${Rrapp} 0 0 1 ${(Ox + Rrapp).toFixed(1)} ${Oy}' fill='none' stroke='#94a3b8' stroke-width='1.2'/>`;

    return `<svg data-v='${salt}' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 500 270' width='500' height='270'>`
        + sector(thC, thB, Rsect, COLOR_LEFT)
        + sector(thB, thA, Rsect, COLOR_LEFT)
        + sector(0, thE, Rsect, COLOR_RIGHT)
        + sector(thE, thD, Rsect, COLOR_RIGHT)
        + rapporteur
        + `<line x1='${pA.x.toFixed(1)}' y1='${Oy}' x2='${pF.x.toFixed(1)}' y2='${Oy}' stroke='black' stroke-width='1.8'/>`
        + ray(pB) + ray(pC) + ray(pD) + ray(pE)
        + `<circle cx='${Ox}' cy='${Oy}' r='2.5' fill='black'/>`
        + lbl(pA, 'A', -14, 5) + lbl(pB, 'B', -14, -3) + lbl(pC, 'C', -5, -6)
        + lbl(pD, 'D', 3, -6) + lbl(pE, 'E', 5, -3) + lbl(pF, 'F', 5, 5)
        + `<text x='${Ox + 4}' y='${Oy + 18}' font-size='14' font-style='italic'>O</text>`
        + degTxt(180 - a / 2, Rsect - 18, `${a}°`) + degTxt(180 - 3 * a / 2, Rsect - 18, `${a}°`)
        + degTxt(3 * c / 2, Rsect - 18, `${c}°`) + degTxt(c / 2, Rsect - 18, `${c}°`)
        + `</svg>`;
};

const VRAIES_NOMMER = [
    `[OB) est la bissectrice de l'angle $\\widehat{AOC}$.`,
    `[OE) est la bissectrice de l'angle $\\widehat{DOF}$.`,
];
const DISTRACTEURS_NOMMER = [
    `[OC) est la bissectrice de l'angle $\\widehat{AOB}$.`,
    `[OD) est la bissectrice de l'angle $\\widehat{COE}$.`,
    `[OE) est la bissectrice de l'angle $\\widehat{COF}$.`,
    `[OB) est la bissectrice de l'angle $\\widehat{AOD}$.`,
    `[OC) est la bissectrice de l'angle $\\widehat{BOD}$.`,
    `[OD) est la bissectrice de l'angle $\\widehat{COF}$.`,
];
const ANGLES_A_NOMMER = [30, 35, 38, 40, 42, 45];
const ANGLES_C_NOMMER = [18, 20, 22, 25, 28, 30];

const genNommerBissectricesQcmMulti = () => {
    const a = randomChoice(ANGLES_A_NOMMER);
    const c = randomChoice(ANGLES_C_NOMMER);
    const salt = randInt(1, 99999);

    const svg = svgNommerBissectrices(a, c, salt);
    const imageData = svgToDataUrl(svg);

    const fausses = shuffle([...DISTRACTEURS_NOMMER]).slice(0, randInt(2, 3));
    const allChoix = shuffle([...VRAIES_NOMMER, ...fausses]);

    const texte = `<p>Sur la figure ci-dessous, nomme les <b>bissectrices</b> tracées. Coche toutes les affirmations correctes.</p>{{element:0}}`;

    const payload = makeQcm(allChoix, [...VRAIES_NOMMER], texte, opts(L_NOMMER), true);
    payload.elements_additionnels = {
        version: 1,
        elements: [{ type: 'image', config: { imageData } }],
    };
    payload._meta.generatorKey = 'nommer_bissectrices_qcm_multi';
    return payload;
};

// ─── Registry & Export ───────────────────────────────────────
const GENERATORS = {
    definition_bissectrice_qcm_unique: () => randomChoice([genDefQcmDefinition, genDefQcmFigure])(),
    definition_bissectrice_texte: genDefSaisie,
    definition_bissectrice_relier: genDefRelier,
    definition_bissectrice_tableau_vf: genDefVF,
    definition_bissectrice_texte_trou_drag: genDefTrouDrag,
    reconnaissance_visuelle_bissectrice_qcm_multi: genReconnaissanceVisuelleQcmMulti,
    nommer_bissectrices_qcm_multi: genNommerBissectricesQcmMulti,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM',
    qcm_multi: 'QCM multi',
    texte: 'Saisie',
    relier: 'Relier',
    tableau_vf: 'Vrai/Faux',
    texte_trou_drag: 'Drag',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS)
        .filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({
            key: k,
            label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k,
        }));

export const CATEGORIES = [
    {
        id: 'definition_bissectrice',
        label: 'Définition de la bissectrice',
        formatsList: buildFormats('definition_bissectrice'),
    },
    {
        id: 'reconnaissance_visuelle_bissectrice',
        label: 'Reconnaître visuellement la bissectrice',
        formatsList: buildFormats('reconnaissance_visuelle_bissectrice'),
    },
    {
        id: 'nommer_bissectrices',
        label: 'Nommer les bissectrices sur une figure codée',
        formatsList: buildFormats('nommer_bissectrices'),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // La clé du REGISTRE fait foi, toujours : c'est elle qui est persistée
    // dans `generator_key` et qui identifie le format. Un `if (!...)` laissait
    // passer des clés locales qui ne correspondaient à aucun format.
    payload._meta.generatorKey = key;
    return payload;
};
